import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { listContent, createContent, updateContent, publishContent, archiveContent, restoreContent, getContent, listMedia, mediaUrl } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { ArrowLeft, Plus, Save, Eye, Archive, RotateCcw, Trash2, CheckCircle2, XCircle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { parseYouTubeId, youtubeThumbnailUrl } from "@/lib/youtube";

const LABELS = {
  articles: { plural: "Articles", singular: "Article", eyebrow: "Editorial" },
  research_themes: { plural: "Research Themes", singular: "Research Theme", eyebrow: "Research" },
  videos: { plural: "Videos", singular: "Video", eyebrow: "Media" },
  events: { plural: "Events", singular: "Event", eyebrow: "Programmes" },
  series: { plural: "Series", singular: "Series", eyebrow: "Collections" },
  authors: { plural: "Authors & Contributors", singular: "Author", eyebrow: "People" },
  legal_pages: { plural: "Legal Pages", singular: "Legal Page", eyebrow: "Legal" },
};

export default function AdminGenericContent() {
  const { user } = useAuth();
  const { ctype } = useParams();
  const nav = useNavigate();
  const info = LABELS[ctype] || { plural: ctype, singular: ctype, eyebrow: "Content" };
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const emptyItem = { title: "", slug: "", summary: "", body: "", category: "", tags: [], translations: { hi: { status: "draft" }, bn: { status: "draft" } } };
  const canEdit = user?.role === "owner" || user?.role === "editor";
  const isOwner = user?.role === "owner";

  const load = useCallback(() => listContent(ctype).then(setItems).catch(() => setItems([])), [ctype]);
  useEffect(() => { load(); setEditing(null); }, [ctype, load]);
  const startEdit = async (it) => {
    const full = await getContent(ctype, it.id).catch(() => it);
    setEditing({ ...emptyItem, ...full, translations: { ...emptyItem.translations, ...(full.translations || {}) } });
  };
  const startNew = () => setEditing({ ...emptyItem });

  const save = async () => {
    if (!editing.title.trim()) return toast.error("Title required");
    try {
      if (editing.id) await updateContent(ctype, editing.id, editing);
      else { const created = await createContent(ctype, editing); setEditing(created); }
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const doPublish = async () => { try { await publishContent(ctype, editing.id); toast.success("Published"); load(); } catch { toast.error("Publish failed (owner only)"); } };
  const doArchive = async () => { if (!window.confirm("Archive this item?")) return; try { await archiveContent(ctype, editing.id); toast.success("Archived"); load(); setEditing(null); } catch { toast.error("Failed"); } };
  const doRestore = async () => { try { await restoreContent(ctype, editing.id); toast.success("Restored to draft"); load(); } catch { toast.error("Failed"); } };

  if (editing) {
    return (
      <div data-testid={`admin-content-editor-${ctype}`}>
        <button onClick={() => setEditing(null)} className="btn-ghost mb-4"><ArrowLeft size={12} /> Back to {info.plural}</button>
        <PageHeader eyebrow={info.eyebrow} title={editing.id ? `Edit ${info.singular}` : `New ${info.singular}`} />
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
          <div className="space-y-4 bg-white border p-6" style={{ borderColor: "var(--gi-border)" }}>
            <div>
              <label className="gi-label">Title *</label>
              <input className="gi-input" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="content-title" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="gi-label">Slug</label><input className="gi-input" value={editing.slug || ""} onChange={e => setEditing({ ...editing, slug: e.target.value })} /></div>
              <div><label className="gi-label">Category / Section</label><input className="gi-input" value={editing.category || ""} onChange={e => setEditing({ ...editing, category: e.target.value })} /></div>
            </div>
            <div><label className="gi-label">Summary</label><textarea rows={2} className="gi-input" value={editing.summary || ""} onChange={e => setEditing({ ...editing, summary: e.target.value })} data-testid="content-summary" /></div>
            <div><label className="gi-label">Body</label><textarea rows={10} className="gi-input" value={editing.body || ""} onChange={e => setEditing({ ...editing, body: e.target.value })} data-testid="content-body" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="gi-label">Author or contributor</label><input className="gi-input" value={editing.author || ""} onChange={e => setEditing({ ...editing, author: e.target.value })} /></div>
              <div><label className="gi-label">Date</label><input type="date" className="gi-input" value={(editing.date || "").slice(0, 10)} onChange={e => setEditing({ ...editing, date: e.target.value })} /></div>
            </div>
            {ctype === "events" && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="gi-label">Starts at</label><input type="datetime-local" className="gi-input" value={editing.starts_at || ""} onChange={e => setEditing({ ...editing, starts_at: e.target.value })} /></div>
                <div><label className="gi-label">Location or online link</label><input className="gi-input" value={editing.location || ""} onChange={e => setEditing({ ...editing, location: e.target.value })} /></div>
              </div>
            )}
            {ctype === "videos" && (
              <VideoFields editing={editing} setEditing={setEditing} />
            )}
            {ctype === "research_themes" && (
              <div><label className="gi-label">Status label</label><select className="gi-input" value={editing.status_label || ""} onChange={e => setEditing({ ...editing, status_label: e.target.value })}>
                <option value="">— select —</option>
                <option>Established Evidence</option>
                <option>Scholarly Debate</option>
                <option>Working Hypothesis</option>
                <option>Author's Interpretation</option>
              </select></div>
            )}
            <div><label className="gi-label">Featured image URL</label><input className="gi-input" value={editing.featured_image_url || ""} onChange={e => setEditing({ ...editing, featured_image_url: e.target.value })} /></div>
            <TranslationsFields editing={editing} setEditing={setEditing} />
          </div>
          <aside className="border bg-white p-6 sticky top-6 h-fit" style={{ borderColor: "var(--gi-border)" }}>
            <div className="font-accent text-[10px] mb-3" style={{ color: "var(--gi-maroon)" }}>Workflow</div>
            <div className="text-sm" data-testid={`content-workflow-${editing.publish_status || "draft"}`}>Current: <strong>{editing.publish_status || "draft"}</strong></div>
            {canEdit && <button onClick={save} className="btn-maroon w-full justify-center mt-6" data-testid="content-save-btn"><Save size={12} /> Save draft</button>}
            {editing.id && isOwner && editing.publish_status !== "published" && editing.publish_status !== "archived" && <button onClick={doPublish} className="btn-gold w-full justify-center mt-3" data-testid="content-publish-btn"><Eye size={12} /> Publish</button>}
            {editing.id && canEdit && editing.publish_status !== "archived" && <button onClick={doArchive} className="btn-ghost w-full justify-center mt-3" data-testid="content-archive-btn"><Archive size={12} /> Archive</button>}
            {editing.id && canEdit && editing.publish_status === "archived" && <button onClick={doRestore} className="btn-ghost w-full justify-center mt-3" data-testid="content-restore-btn"><RotateCcw size={12} /> Restore</button>}
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`admin-content-list-${ctype}`}>
      <PageHeader eyebrow={info.eyebrow} title={info.plural} note="Every item starts as Draft. Publishing requires the Owner." />
      <div className="mt-8 flex justify-end">
        {canEdit && <button onClick={startNew} className="btn-maroon" data-testid="content-new-btn"><Plus size={14} /> New {info.singular}</button>}
      </div>
      <div className="mt-6 border bg-white" style={{ borderColor: "var(--gi-border)" }}>
        {items.length === 0 ? <div className="p-8 text-sm text-center" style={{ color: "var(--gi-muted)" }} data-testid="content-empty">No {info.plural} yet. Click "New" to create the first one.</div> : items.map(it => (
          <div key={it.id} className="grid grid-cols-[1fr_140px_120px] gap-4 px-5 py-4 border-b last:border-b-0 items-center" style={{ borderColor: "var(--gi-divider)" }} data-testid={`content-row-${it.id}`}>
            <div>
              <div className="font-heading text-base" style={{ color: "var(--gi-charcoal)" }}>{it.title}</div>
              <div className="text-xs" style={{ color: "var(--gi-muted)" }}>{it.category} {it.date && `· ${it.date}`}</div>
            </div>
            <div className="text-xs uppercase tracking-[0.14em]" style={{ color: "var(--gi-muted)" }}>{it.publish_status || "draft"}</div>
            <div className="flex justify-end"><button onClick={() => startEdit(it)} className="btn-ghost">Edit</button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TranslationsFields = ({ editing, setEditing }) => (
  <div>
    <label className="gi-label">Translations (approved only)</label>
    <div className="border-t pt-4 space-y-4" style={{ borderColor: "var(--gi-divider)" }}>
      {["hi", "bn"].map(l => (
        <div key={l}>
          <div className="flex items-center gap-3 mb-2">
            <span className="font-heading text-base" style={{ color: "var(--gi-maroon)" }}>{l === "hi" ? "हिन्दी" : "বাংলা"}</span>
            <select className="gi-input" style={{ width: "auto" }} value={editing.translations?.[l]?.status || "draft"} onChange={e => setEditing({ ...editing, translations: { ...editing.translations, [l]: { ...(editing.translations?.[l] || {}), status: e.target.value } } })} data-testid={`content-trans-status-${l}`}>
              <option value="draft">Draft</option>
              <option value="under_review">Under Review</option>
              <option value="published">Published</option>
            </select>
          </div>
          <input className="gi-input mb-2" placeholder="Title" value={editing.translations?.[l]?.title || ""} onChange={e => setEditing({ ...editing, translations: { ...editing.translations, [l]: { ...(editing.translations?.[l] || {}), title: e.target.value } } })} data-testid={`content-trans-title-${l}`} />
          <textarea rows={2} className="gi-input mb-2" placeholder="Summary" value={editing.translations?.[l]?.summary || ""} onChange={e => setEditing({ ...editing, translations: { ...editing.translations, [l]: { ...(editing.translations?.[l] || {}), summary: e.target.value } } })} />
          <textarea rows={4} className="gi-input" placeholder="Body" value={editing.translations?.[l]?.body || ""} onChange={e => setEditing({ ...editing, translations: { ...editing.translations, [l]: { ...(editing.translations?.[l] || {}), body: e.target.value } } })} />
        </div>
      ))}
    </div>
  </div>
);

const VideoFields = ({ editing, setEditing }) => {
  const [thumbs, setThumbs] = useState([]);
  useEffect(() => {
    listMedia({ kind: "trailer_thumbnail" }).then(setThumbs).catch(() => setThumbs([]));
  }, []);
  const url = editing.external_url || "";
  const ytId = useMemo(() => parseYouTubeId(url), [url]);
  const autoThumb = useMemo(() => youtubeThumbnailUrl(url), [url]);
  const previewThumb = editing.thumbnail_url ? mediaUrl(editing.thumbnail_url) : autoThumb;
  return (
    <>
      <div>
        <label className="gi-label">YouTube URL *</label>
        <input
          className="gi-input"
          placeholder="https://www.youtube.com/watch?v=… or https://youtu.be/…"
          value={url}
          onChange={e => setEditing({ ...editing, external_url: e.target.value })}
          data-testid="video-url"
        />
        <div className="mt-1 text-xs flex items-center gap-1" style={{ color: ytId ? "var(--gi-green)" : "var(--gi-muted)" }}>
          {ytId
            ? <><CheckCircle2 size={12} /> Recognised YouTube video · id <code>{ytId}</code></>
            : url
              ? <><XCircle size={12} style={{ color: "var(--gi-maroon)" }} /> Not a recognised YouTube URL. The public site will hide this card until fixed.</>
              : <>Direct video uploads are not supported — use a YouTube URL.</>}
        </div>
      </div>
      <div>
        <label className="gi-label">Thumbnail (optional — falls back to YouTube's auto image)</label>
        <div className="grid gap-3 grid-cols-4">
          <label className="border p-2 cursor-pointer flex flex-col items-center gap-1 text-xs" style={{ borderColor: !editing.thumbnail_url ? "var(--gi-maroon)" : "var(--gi-border)" }}>
            <input type="radio" checked={!editing.thumbnail_url} onChange={() => setEditing({ ...editing, thumbnail_url: "" })} className="sr-only" />
            {autoThumb
              ? <img src={autoThumb} alt="Auto YouTube thumbnail" className="w-full aspect-video object-cover" />
              : <div className="w-full aspect-video bg-[var(--gi-ivory-2)] flex items-center justify-center"><ImageIcon size={20} style={{ color: "var(--gi-muted)" }} /></div>}
            <span>Auto</span>
          </label>
          {thumbs.slice(0, 7).map(o => (
            <label key={o.id} className="border p-2 cursor-pointer flex flex-col items-center gap-1 text-xs" style={{ borderColor: editing.thumbnail_url === o.url ? "var(--gi-maroon)" : "var(--gi-border)" }}>
              <input type="radio" checked={editing.thumbnail_url === o.url} onChange={() => setEditing({ ...editing, thumbnail_url: o.url })} className="sr-only" />
              <img src={mediaUrl(o.url)} alt={o.alt_text || o.filename} className="w-full aspect-video object-cover" />
              <span className="truncate w-full">{o.filename}</span>
            </label>
          ))}
        </div>
        <p className="mt-1 text-xs" style={{ color: "var(--gi-muted)" }}>Upload thumbnails to Media Library with Kind = "trailer_thumbnail" to see them here.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="gi-label">Thumbnail alt text</label>
          <input className="gi-input" value={editing.alt_text || ""} onChange={e => setEditing({ ...editing, alt_text: e.target.value })} data-testid="video-alt" />
        </div>
        <div>
          <label className="gi-label">Display order (lower shows first)</label>
          <input type="number" className="gi-input" value={editing.order ?? ""} onChange={e => setEditing({ ...editing, order: e.target.value === "" ? undefined : Number(e.target.value) })} data-testid="video-order" />
        </div>
      </div>
      {previewThumb && (
        <div>
          <label className="gi-label">Preview</label>
          <img src={previewThumb} alt="Video card preview" className="max-w-md border" style={{ borderColor: "var(--gi-border)" }} />
        </div>
      )}
    </>
  );
};

